import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErrorNotice } from '@/components/common/ErrorNotice';
import {
  INITIAL_LOGIN_PATTERN,
  type InitialLoginPattern,
} from '@/constants/auth';
import { ROUTE_PATH } from '@/constants/routes';
import { MEMBERSHIP_TYPES } from '@/constants/users';
import { useApiErrorTranslation } from '@/hooks/useApiErrorTranslation';
import { useApiRequest } from '@/hooks/useApiRequest';
import { useSuiteLoungeFlow } from '@/hooks/useSuiteLoungeFlow';

interface LocationState {
  email?: string;
  initial_login_pattern: InitialLoginPattern;
}

interface NameFormData {
  firstName: string;
  lastName: string;
}

export const HasBitkeyAccount = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { isSuiteLoungeFlow, withEntry } = useSuiteLoungeFlow();
  const { translateApiError } = useApiErrorTranslation();
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<string[]>([]);
  const { isRequesting, execute } = useApiRequest('POST', 'CreateContract');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<NameFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
    },
  });

  // location.stateからemailを取得（前画面から渡される想定）
  const state = location.state as LocationState | null;
  const email = state?.email;
  const initial_login_pattern = state?.initial_login_pattern;

  // 遷移元でデータがセットされていない場合は想定フローではないのでログイン画面に戻す
  useEffect(() => {
    if (!email) {
      navigate(withEntry(ROUTE_PATH.LOGIN));
    }
  }, [email, navigate, withEntry]);

  const onSubmit = async (data: NameFormData) => {
    if (!email) return;

    setErrorMessage('');

    const membership_type = isSuiteLoungeFlow
      ? MEMBERSHIP_TYPES.SUITE_LOUNGE
      : MEMBERSHIP_TYPES.ANA_LOUNGE;

    const result = await execute({
      email,
      family_name_jp: data.lastName,
      first_name_jp: data.firstName,
      membership_type,
    });

    if (result.ok) {
      navigate(ROUTE_PATH.PASSWORD_SETTING_CONFIRM, { state: { email } });
    } else {
      const { message, detailMessages } = translateApiError(
        result.error,
        result.details,
      );
      setErrorMessage(message);
      setErrorDetails(detailMessages);
    }
  };

  const handleNext = () => {
    if (!email) return;
    // existing_customerの場合は直接遷移
    navigate(ROUTE_PATH.PASSWORD_SETTING_CONFIRM, { state: { email } });
  };

  if (!email) {
    return null;
  }

  return (
    <div className="page p-auth page_a2">
      <main className="page__main">
        <div className="page__content">
          <section className="l-section">
            <div className="l-section__line">
              <div className="l-container">
                <div className="l-hero">
                  <img
                    className="l-hero__img"
                    src="/Common_Title.jpg"
                    alt="LOUNGE RESERVATION"
                  />
                </div>
                <div className="l-grid">
                  <section className="l-card">
                    <div className="l-card__body">
                      <div className="account-status">
                        <div className="account-status__headline">
                          {t('hasBitkeyAccount.headline')}
                        </div>
                        <div className="account-status__email">{email}</div>
                        {initial_login_pattern ===
                          INITIAL_LOGIN_PATTERN.WORKHUB_NO_CUSTOMER && (
                          <div className="c-auth c-auth--login">
                            <div className="c-auth__fields">
                              {/* お名前 */}
                              <div className="c-auth__field c-auth__field--name">
                                <div className="c-auth__name">
                                  <label className="c-auth__label c-auth__label--family">
                                    <span className="c-auth__label-text">
                                      {t('signup.lastName')}
                                    </span>
                                    <input
                                      className="c-auth__input c-auth__input--family"
                                      type="text"
                                      placeholder={t(
                                        'signup.lastNamePlaceholder',
                                      )}
                                      {...register('lastName', {
                                        required: t(
                                          'signup.validation.lastNameRequired',
                                        ),
                                      })}
                                    />
                                  </label>
                                  <label className="c-auth__label c-auth__label--given">
                                    <span className="c-auth__label-text">
                                      {t('signup.firstName')}
                                    </span>
                                    <input
                                      className="c-auth__input c-auth__input--given"
                                      type="text"
                                      placeholder={t(
                                        'signup.firstNamePlaceholder',
                                      )}
                                      {...register('firstName', {
                                        required: t(
                                          'signup.validation.firstNameRequired',
                                        ),
                                      })}
                                    />
                                  </label>
                                </div>
                                {errors.lastName && (
                                  <div className="common-error">
                                    {errors.lastName.message}
                                  </div>
                                )}
                                {errors.firstName && (
                                  <div className="common-error">
                                    {errors.firstName.message}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        <div className="c-auth">
                          <div className="account-status__note">
                            {initial_login_pattern ===
                            INITIAL_LOGIN_PATTERN.WORKHUB_NO_CUSTOMER
                              ? t('hasBitkeyAccount.noCustomerNote')
                              : t('hasBitkeyAccount.noContractNote')}
                          </div>
                        </div>
                      </div>
                      {errorMessage && (
                        <ErrorNotice
                          message={errorMessage}
                          details={errorDetails}
                        />
                      )}
                      <div className="c-auth__actions">
                        <button
                          onClick={
                            initial_login_pattern ===
                            INITIAL_LOGIN_PATTERN.WORKHUB_NO_CUSTOMER
                              ? handleSubmit(onSubmit)
                              : handleNext
                          }
                          className="link-button link-button--next"
                          disabled={isRequesting}
                        >
                          {isRequesting
                            ? t('common.btn.nextLoading')
                            : t('common.btn.next')}
                        </button>
                      </div>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};
